import { getPaletteColor } from "@/lib/color-palettes";
import type { AuditStatus, UIAudit, AuditStatusConfig } from "@/packages/types";
import type { AuditAction } from "./use-audit-status-change";

export type { AuditStatusConfig } from "@/packages/types";

export const AUDIT_STATUS_SLOT: Record<AuditStatus, number> = {
  pending: 1,
  in_progress: 2,
  submitted: 3,
  approved: 4,
  rejected: 5,
  archived: 6,
};

/**
 * DEPRECATED: Use useAuditStatusLabels() hook instead.
 * Returns audit status config with color only (label will be undefined).
 * This is kept for backward compatibility with non-React code paths.
 */
export function getAuditStatusConfig(status: AuditStatus): AuditStatusConfig {
  const color = getPaletteColor("audit-status", AUDIT_STATUS_SLOT[status]);
  return {
    label: "", // Use useAuditStatusLabels() hook for translated labels
    dotColor: color,
    badgeBg: `${color}26`, // ~15% opacity
    badgeText: color,
    sortOrder: AUDIT_STATUS_SLOT[status],
  };
}

/**
 * DEPRECATED: Use useAuditStatusLabels() hook instead.
 * Backward-compatible record that resolves palette colors on each property access.
 * Consumers should migrate to the useAuditStatusLabels() hook.
 */
export const AUDIT_STATUS_CONFIG: Record<AuditStatus, AuditStatusConfig> =
  // Proxy seed: empty object typed for consumer inference
  new Proxy({} as Record<AuditStatus, AuditStatusConfig>, {
    get(_target, prop: string) {
      if (prop in AUDIT_STATUS_SLOT) {
        // Proxy get receives string|symbol; narrowed by `in` check above
        return getAuditStatusConfig(prop as AuditStatus);
      }
      return undefined;
    },
    ownKeys() {
      return Object.keys(AUDIT_STATUS_SLOT);
    },
    getOwnPropertyDescriptor(_target, prop: string) {
      if (prop in AUDIT_STATUS_SLOT) {
        return {
          configurable: true,
          enumerable: true,
          writable: false,
          // Proxy get receives string|symbol; narrowed by `in` check above
          value: getAuditStatusConfig(prop as AuditStatus),
        };
      }
      return undefined;
    },
  });

export const SORTED_STATUSES: AuditStatus[] = (
  Object.entries(AUDIT_STATUS_CONFIG) as [AuditStatus, AuditStatusConfig][]
)
  .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
  .map(([status]) => status);

export function groupAuditsByStatus(
  audits: UIAudit[],
): Map<AuditStatus, UIAudit[]> {
  const map = new Map<AuditStatus, UIAudit[]>();

  for (const status of SORTED_STATUSES) {
    map.set(status, []);
  }

  for (const audit of audits) {
    const group = map.get(audit.status);
    if (group) {
      group.push(audit);
    }
  }

  return map;
}

export function getAuditColumns(
  audits: UIAudit[],
): { id: AuditStatus; title: string; audits: UIAudit[] }[] {
  const grouped = groupAuditsByStatus(audits);

  return SORTED_STATUSES.map((status) => ({
    id: status,
    title: AUDIT_STATUS_CONFIG[status].label,
    audits: grouped.get(status) ?? [],
  }));
}

export interface AuditTransitionConfig {
  /** Status this action moves the audit to */
  targetStatus: AuditStatus;
  /** Whether this primary action is fully implemented in MVP */
  mvp: boolean;
  /** Whether rejection reason is required (only for reject) */
  requiresReason?: boolean;
}

export const AUDIT_TRANSITION_CONFIG: Partial<
  Record<AuditStatus, AuditTransitionConfig>
> = {
  pending: {
    targetStatus: "in_progress",
    mvp: true,
  },
  in_progress: {
    targetStatus: "submitted",
    mvp: true,
  },
  submitted: {
    targetStatus: "approved",
    mvp: true,
  },
  approved: {
    targetStatus: "archived",
    mvp: true,
  },
  rejected: {
    targetStatus: "submitted",
    mvp: true,
  },
  archived: {
    targetStatus: "in_progress",
    mvp: true,
  },
};

export const VALID_TRANSITIONS_KEYS: Record<string, { action: AuditAction }> = {
  // Forward transitions
  "pending->in_progress": { action: "move_back" },
  "in_progress->submitted": { action: "submit" },
  "submitted->approved": { action: "approve" },
  // Backward transitions
  "in_progress->pending": { action: "move_back" },
  "submitted->in_progress": { action: "move_back" },
  // Non-adjacent business-critical transitions
  "submitted->rejected": { action: "reject" },
  "rejected->submitted": { action: "resubmit" },
  "approved->submitted": { action: "move_back" },
  "approved->archived": { action: "archive" },
  "in_progress->archived": { action: "archive" },
  "archived->in_progress": { action: "unarchive" },
};

export function isValidStatusTransition(
  source: AuditStatus,
  target: AuditStatus,
): boolean {
  return `${source}->${target}` in VALID_TRANSITIONS_KEYS;
}

export function getActionForTransition(
  source: AuditStatus,
  target: AuditStatus,
): AuditAction | undefined {
  return VALID_TRANSITIONS_KEYS[`${source}->${target}`]?.action;
}

/**
 * DEPRECATED: Use useAuditTransitionLabels() hook instead.
 * Returns the action for a transition without label (label is empty string).
 * Kept for backward compatibility with non-React code.
 */
export function getTransitionLabel(
  _source: AuditStatus,
  _target: AuditStatus,
): string {
  // Return empty string; labels should be retrieved from the hook
  return "";
}

/**
 * DEPRECATED: Use useAuditTransitionLabels() hook instead.
 * Returns rollback transitions without labels.
 * Kept for backward compatibility; use the hook for translated labels.
 */
export function getRollbackTransitions(
  status: AuditStatus,
): { targetStatus: AuditStatus; action: AuditAction }[] {
  const rollbacks: { targetStatus: AuditStatus; action: AuditAction }[] = [];
  for (const [key, value] of Object.entries(VALID_TRANSITIONS_KEYS)) {
    const [source, target] = key.split("->") as [AuditStatus, AuditStatus];
    if (source !== status) continue;
    // Skip the primary forward transition (already shown as main button)
    const primaryTarget = AUDIT_TRANSITION_CONFIG[status]?.targetStatus;
    if (target === primaryTarget) continue;
    rollbacks.push({
      targetStatus: target,
      action: value.action,
    });
  }
  return rollbacks;
}

export function formatDeadline(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
